import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface RiskRow {
  readonly aiRiskId: string;
  readonly riskCode: string;
  readonly riskTitle: string;
  readonly riskType: string;
  readonly severity: string;
  readonly likelihood: string;
  readonly riskScore: number;
  readonly status: string;
  readonly nextReviewAt: string | null;
}

const RISK_TYPES = ['HALLUCINATION', 'DATA_LEAKAGE', 'COMPLIANCE', 'BIAS', 'SECURITY', 'MISUSE', 'OPERATIONAL'];
const SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const LIKELIHOODS = ['RARE', 'UNLIKELY', 'POSSIBLE', 'LIKELY', 'ALMOST_CERTAIN'];
const STATUSES = ['IDENTIFIED', 'ASSESSED', 'MITIGATING', 'MITIGATED', 'ACCEPTED', 'CLOSED'];
const PAGE = 25;

/**
 * M38 — AI Risk Dashboard (§21). The AI risk register (T512) covering the seven
 * governed AI risk types (hallucination, data leakage, compliance, bias, security,
 * misuse, operational). Risk officers register and re-assess risks (RISK_MANAGE);
 * the residual risk score is computed from severity × likelihood, and each risk
 * carries a scheduled next-review date (RISK_REVIEW_INTERVAL_DAYS). Filterable by
 * type, severity and lifecycle status. Tenant isolation is enforced server-side
 * (LAW 4). A 403 surfaces a clear permission message.
 */
export function AiRiskDashboard(): ReactNode {
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<RiskRow[]>([]);
  const [total, setTotal] = useState(0);
  const [riskType, setRiskType] = useState('');
  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [title, setTitle] = useState('');
  const [newType, setNewType] = useState(RISK_TYPES[0]);
  const [newSeverity, setNewSeverity] = useState('MEDIUM');
  const [newLikelihood, setNewLikelihood] = useState('POSSIBLE');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [created, setCreated] = useState<RiskRow | null>(null);

  // §21 — re-assess (status) / remove a registered risk (RISK_MANAGE).
  const [rowError, setRowError] = useState<string | null>(null);
  const [rowNotice, setRowNotice] = useState<string | null>(null);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState('IDENTIFIED');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (riskType) params.set('riskType', riskType);
    if (severity) params.set('severity', severity);
    if (status) params.set('status', status);
    params.set('limit', String(PAGE));
    params.set('offset', String(page * PAGE));
    apiRequest<{ data: RiskRow[]; total: number }>(`/v1/ai/risks?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, riskType, severity, status, page, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setCreated(null);
    if (!title.trim()) { setActionError(t('m38.risk.titleRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: RiskRow }>(`/v1/ai/risks`, {
        method: 'POST',
        body: { riskTitle: title.trim(), riskType: newType, severity: newSeverity, likelihood: newLikelihood },
        token,
      });
      setCreated(r.data);
      setTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m38.risk.notAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const permMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? t('m38.risk.notAuthorised')
      : (err as Error).message;

  const startEdit = (r: RiskRow): void => {
    setRowError(null);
    setRowNotice(null);
    setEditId(r.aiRiskId);
    setEditStatus(r.status);
  };

  const saveStatus = async (id: string): Promise<void> => {
    setRowError(null);
    setRowNotice(null);
    setBusyRowId(id);
    try {
      await apiRequest(`/v1/ai/risks/${id}`, { method: 'PUT', body: { status: editStatus }, token });
      setEditId(null);
      setRowNotice(t('m38.risk.reassessed'));
      setReload((n) => n + 1);
    } catch (err) {
      setRowError(permMessage(err));
    } finally {
      setBusyRowId(null);
    }
  };

  const removeRisk = async (id: string): Promise<void> => {
    setRowError(null);
    setRowNotice(null);
    setBusyRowId(id);
    try {
      await apiRequest(`/v1/ai/risks/${id}`, { method: 'DELETE', token });
      setEditId(null);
      setRowNotice(t('m38.risk.removed'));
      setReload((n) => n + 1);
    } catch (err) {
      setRowError(permMessage(err));
    } finally {
      setBusyRowId(null);
    }
  };

  return (
    <main aria-label="ai-risk-dashboard">
      <h1>{t('m38.risk.title')}</h1>

      <section aria-label="create-risk">
        <h2>{t('m38.risk.registerHeading')}</h2>
        <form aria-label="risk-form" onSubmit={(e) => void submit(e)}>
          <label>{t('m38.common.title')}<input aria-label="field-title" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <label>{t('m38.common.type')}
            <select aria-label="field-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {RISK_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m38.common.severity')}
            <select aria-label="field-severity" value={newSeverity} onChange={(e) => setNewSeverity(e.target.value)}>
              {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>{t('m38.risk.likelihood')}
            <select aria-label="field-likelihood" value={newLikelihood} onChange={(e) => setNewLikelihood(e.target.value)}>
              {LIKELIHOODS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-risk" disabled={busy}>{busy ? t('m38.common.saving') : t('m38.risk.registerBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="risk-error">{actionError}</p>}
        {created && <p aria-label="risk-created">{t('m38.risk.created', { code: created.riskCode, score: created.riskScore })}</p>}
      </section>

      <section aria-label="risk-filters">
        <label>{t('m38.common.type')}
          <select aria-label="filter-type" value={riskType} onChange={(e) => { setRiskType(e.target.value); setPage(0); }}>
            <option value="">{t('m38.common.allTypes')}</option>
            {RISK_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
        <label>{t('m38.common.severity')}
          <select aria-label="filter-severity" value={severity} onChange={(e) => { setSeverity(e.target.value); setPage(0); }}>
            <option value="">{t('m38.risk.allSeverities')}</option>
            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>{t('m38.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
            <option value="">{t('m38.common.allStatuses')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="risk-total">{t('m38.risk.total', { count: total })}</p>
          {rowNotice && <p aria-label="risk-row-notice">{rowNotice}</p>}
          {rowError && <p role="alert" aria-label="risk-row-error">{rowError}</p>}
          <DataTable<RiskRow>
            ariaLabel="risk-table"
            testId="risk-table"
            rowKey={(r) => r.aiRiskId}
            rowDataAttrs={(r) => ({ 'data-risk': r.riskCode })}
            emptyMessage={t('m38.risk.noneMatch')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m38.common.code'), render: (r) => r.riskCode },
              { key: 'title', header: t('m38.common.title'), render: (r) => r.riskTitle },
              { key: 'type', header: t('m38.common.type'), render: (r) => r.riskType },
              { key: 'severity', header: t('m38.common.severity'), render: (r) => r.severity },
              { key: 'likelihood', header: t('m38.risk.likelihood'), render: (r) => r.likelihood },
              { key: 'score', header: t('m38.risk.colScore'), cellAriaLabel: (r) => `score-${r.riskCode}`, render: (r) => number(r.riskScore) },
              { key: 'status', header: t('m38.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m38.common.actions'), render: (r) => (
                editId === r.aiRiskId ? (
                  <>
                    <select aria-label={`edit-status-${r.riskCode}`} value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <Button variant="primary" type="button" aria-label={`save-status-${r.riskCode}`} disabled={busyRowId === r.aiRiskId} onClick={() => void saveStatus(r.aiRiskId)}>{t('common.save')}</Button>
                    <Button variant="danger" type="button" aria-label={`cancel-status-${r.riskCode}`} onClick={() => setEditId(null)}>{t('common.cancel')}</Button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" type="button" aria-label={`reassess-${r.riskCode}`} onClick={() => startEdit(r)}>{t('m38.risk.reassess')}</Button>
                    <Button variant="danger" type="button" aria-label={`delete-${r.riskCode}`} disabled={busyRowId === r.aiRiskId} onClick={() => void removeRisk(r.aiRiskId)}>{t('common.delete')}</Button>
                  </>
                )
              ) },
            ]}
          />

          <nav aria-label="risk-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m38.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m38.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m38.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
